import { useEffect, useState } from "react";
import {
  IonButton,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonHeader,
  IonToast,
  IonGrid,
  IonRow,
  IonCol,
  IonSpinner,
  IonText,
} from "@ionic/react";
import { useRecoilValue } from "recoil";
import { userProfileState } from "../services/state.service";
import {
  addSalesPlan,
  getAllSalesActivities,
  getMonthlySalesPlanForMe,
  updateSalesPlan,
} from "../services/sales.service";
import { createTarget, getTargetForMe, updateTarget } from "../services/target.service";
import { getProductivityScore } from "../services/territory.service";
import { handleErrors } from "../services/common.service";

interface PlanOption {
  id: string;
  description: string;
  category?: "process" | "outcome";
}

interface Activities {
  [key: string]: number[];
}

const defaultTarget = () => ({
  revenue: { target: 0, actual: 0 },
  installation: { target: 0, actual: 0 },
  collections: {
    recievables: { target: 0, actual: 0 },
    freshSales: { target: 0, actual: 0 },
  },
});

// Rewritten self-service version of the old (dead, and already broken --
// it read `.estimation` fields the Target model never had) region-picker
// flow. This resolves the logged-in employee's own territory/farmUserId
// instead of asking them to pick a region, matching the new
// territory+employee-keyed MonthlySalesPlan/Target schema.
const CreateSalesPlan = () => {
  const userProfile: any = useRecoilValue(userProfileState);
  const territoryId = userProfile?.territoryId;
  const farmUserId = userProfile?.farmUserId;

  const now = new Date();
  const month = now.getMonth() + 1;
  const year = now.getFullYear();

  const [planOptions, setPlanOptions] = useState<PlanOption[]>([]);
  const [activites, setActivites] = useState<Activities>({});
  const [planId, setPlanId] = useState<string | null>(null);
  const [targetId, setTargetId] = useState<string | null>(null);
  const [target, setTarget] = useState<any>(defaultTarget());
  // Read-only "completed so far" total per type, from the same
  // ProductivityScoreService already used by the Territory Dashboard.
  const [actualByType, setActualByType] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [onSuccess, setOnSuccess] = useState("");
  const [onError, setOnError] = useState("");

  useEffect(() => {
    if (!territoryId || !farmUserId) {
      setLoading(false);
      return;
    }
    let mounted = true;

    Promise.all([
      getAllSalesActivities(),
      getMonthlySalesPlanForMe(territoryId, farmUserId, month, year),
      getTargetForMe(territoryId, farmUserId, String(month), String(year)),
      getProductivityScore(territoryId, farmUserId, month, year).catch(() => null),
    ])
      .then(([types, plans, targets, score]) => {
        if (!mounted) return;
        const typeList: PlanOption[] = types ?? [];
        setPlanOptions(typeList);

        const existingPlan = plans?.[0];
        setPlanId(existingPlan?.id ?? null);
        const initialActivities: Activities = {};
        typeList.forEach((t) => {
          initialActivities[t.id] = existingPlan?.plans?.[t.id] ?? [0, 0, 0];
        });
        setActivites(initialActivities);

        const existingTarget = targets?.[0];
        setTargetId(existingTarget?.id ?? null);
        setTarget(existingTarget ?? defaultTarget());

        const actuals: Record<string, number> = {};
        [...(score?.processDetail ?? []), ...(score?.outcomeDetail ?? [])].forEach((d: any) => {
          const match = typeList.find((t) => t.description === d.type);
          if (match) actuals[match.id] = d.actual;
        });
        setActualByType(actuals);
      })
      .finally(() => mounted && setLoading(false));

    return () => {
      mounted = false;
    };
  }, [territoryId, farmUserId, month, year]);

  const handleInputChange = (key: string, index: number, value: string) => {
    setActivites((prev) => ({
      ...prev,
      [key]: prev[key].map((item, i) => (i === index ? parseFloat(value) || 0 : item)),
    }));
  };

  const handleTargetChange = (field: "revenue" | "installation", value: string) => {
    setTarget((prev: any) => ({
      ...prev,
      [field]: { ...prev[field], target: parseFloat(value) || 0 },
    }));
  };

  const submit = async () => {
    if (!territoryId || !farmUserId) return;
    setSaving(true);
    try {
      if (!planId) {
        await addSalesPlan({ territoryId, farmUserId, month, year, plans: activites }).then(handleErrors);
      } else {
        await updateSalesPlan(planId, { plans: activites }).then(handleErrors);
      }

      if (!targetId) {
        await createTarget({
          territoryId,
          farmUserId,
          month: String(month),
          year: String(year),
          ...target,
        }).then(handleErrors);
      } else {
        await updateTarget(targetId, target).then(handleErrors);
      }

      setOnSuccess("Plan saved");
    } catch (err: any) {
      setOnError(err?.message ?? "Failed to save plan");
    } finally {
      setSaving(false);
    }
  };

  if (!territoryId || !farmUserId) {
    return (
      <IonItem>
        <IonLabel className="ion-text-wrap">
          <IonText color="medium">
            You're not assigned to a territory yet. Contact your admin to get assigned before you
            can submit a monthly plan.
          </IonText>
        </IonLabel>
      </IonItem>
    );
  }

  if (loading) {
    return (
      <IonItem lines="none">
        <IonSpinner />
      </IonItem>
    );
  }

  const processTypes = planOptions.filter((t) => t.category !== "outcome");
  const outcomeTypes = planOptions.filter((t) => t.category === "outcome");

  const renderTypeRow = (type: PlanOption) => (
    <IonList key={type.id}>
      <IonItem>
        <IonLabel>{type.description}</IonLabel>
      </IonItem>
      <IonGrid>
        <IonRow style={{ alignItems: "baseline" }}>
          {(activites[type.id] ?? [0, 0, 0]).map((value, index) => (
            <IonCol key={index} size="4">
              <IonItem style={{ position: "relative", paddingRight: "2px" }}>
                <IonRow style={{ alignItems: "center" }}>
                  {index === 0 && actualByType[type.id] !== undefined && (
                    <IonLabel style={{ marginRight: "4px" }}>{`${actualByType[type.id]} done /`}</IonLabel>
                  )}
                  <IonInput
                    type="number"
                    step="1"
                    min="0"
                    value={value}
                    onIonChange={(e) => handleInputChange(type.id, index, e.detail.value!)}
                  />
                </IonRow>
              </IonItem>
            </IonCol>
          ))}
        </IonRow>
      </IonGrid>
    </IonList>
  );

  return (
    <>
      <IonItem>
        <IonLabel>
          <h2>
            {now.toLocaleString("default", { month: "long" })} {year}
          </h2>
        </IonLabel>
      </IonItem>

      <IonItem style={{ alignItems: "center" }}>
        <IonHeader style={{ fontSize: 23 }}>Process</IonHeader>
      </IonItem>
      {processTypes.map(renderTypeRow)}

      <IonItem style={{ alignItems: "center" }}>
        <IonHeader style={{ fontSize: 23 }}>Outcome</IonHeader>
      </IonItem>
      {outcomeTypes.map(renderTypeRow)}

      <IonItem style={{ alignItems: "center" }}>
        <IonHeader style={{ fontSize: 23 }}>Targets</IonHeader>
      </IonItem>
      <IonItem>
        <IonLabel position="stacked">Revenue</IonLabel>
        <IonInput
          type="number"
          value={target?.revenue?.target}
          onIonChange={(e) => handleTargetChange("revenue", e.detail.value!)}
        />
      </IonItem>
      <IonItem>
        <IonLabel position="stacked">Installation</IonLabel>
        <IonInput
          type="number"
          value={target?.installation?.target}
          onIonChange={(e) => handleTargetChange("installation", e.detail.value!)}
        />
      </IonItem>

      <IonButton disabled={saving} expand="block" onClick={submit}>
        {saving ? "Saving..." : "Save Plan"}
      </IonButton>

      <IonToast
        isOpen={!!onSuccess}
        onDidDismiss={() => setOnSuccess("")}
        message={onSuccess}
        duration={2000}
        color="success"
      />
      <IonToast
        isOpen={!!onError}
        onDidDismiss={() => setOnError("")}
        message={onError}
        duration={2000}
        color="danger"
      />
    </>
  );
};

export default CreateSalesPlan;
